/**
 * Google Static Maps API helpers + Mercator projection math.
 *
 * The fish-tank scene uses a single Static Maps image as the texture on the
 * bottom face of the cube. We need to know the *exact* geographic bounds the
 * image covers so we can project lat/lng of every trackpoint into the same
 * local 2D coordinate space and have them line up with the map underneath.
 */

const TILE_SIZE = 256
const STATIC_MAPS_BASE = 'https://maps.googleapis.com/maps/api/staticmap'
/** Max free tier image dimension. With scale=2 we get 1280×1280 effective. */
const MAX_IMAGE_DIM = 640

export type LatLng = { lat: number; lng: number }
export type LatLngBounds = { north: number; south: number; east: number; west: number }

/**
 * Compute the geographic bounding box of a set of points, with optional
 * padding (as a fraction of the box dimensions, default 15%).
 */
export function computeBounds(points: LatLng[], paddingFraction = 0.15): LatLngBounds | null {
  if (points.length === 0) return null
  let north = -Infinity
  let south = Infinity
  let east = -Infinity
  let west = Infinity
  for (const p of points) {
    if (p.lat > north) north = p.lat
    if (p.lat < south) south = p.lat
    if (p.lng > east) east = p.lng
    if (p.lng < west) west = p.lng
  }
  // Guard against zero-area bounds (single point) — give it ~200m of padding.
  if (north === south) {
    north += 0.001
    south -= 0.001
  }
  if (east === west) {
    east += 0.001
    west -= 0.001
  }
  const dLat = (north - south) * paddingFraction
  const dLng = (east - west) * paddingFraction
  return {
    north: north + dLat,
    south: south - dLat,
    east: east + dLng,
    west: west - dLng,
  }
}

/** Center of a bbox. */
export function boundsCenter(b: LatLngBounds): LatLng {
  return { lat: (b.north + b.south) / 2, lng: (b.east + b.west) / 2 }
}

/**
 * Standard "fit a bbox into a width×height image" zoom calculation.
 * Returns an integer zoom level.
 */
export function fitBoundsZoom(bounds: LatLngBounds, width: number, height: number): number {
  function latRad(lat: number): number {
    const sin = Math.sin((lat * Math.PI) / 180)
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
  }
  const latFraction = (latRad(bounds.north) - latRad(bounds.south)) / Math.PI
  const lngDiff = bounds.east - bounds.west
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360
  const latZoom = Math.floor(Math.log2(height / TILE_SIZE / latFraction))
  const lngZoom = Math.floor(Math.log2(width / TILE_SIZE / lngFraction))
  return Math.max(0, Math.min(latZoom, lngZoom, 21))
}

/**
 * Given a center + zoom + image size, return the actual lat/lng bounds
 * the image will cover. This is what we project our points against.
 */
export function imageBoundsAt(center: LatLng, zoom: number, width: number, height: number): LatLngBounds {
  const scale = TILE_SIZE * Math.pow(2, zoom)
  // Center → world pixel coordinates (Web Mercator).
  const sin = Math.sin((center.lat * Math.PI) / 180)
  const centerX = scale * (center.lng / 360 + 0.5)
  const centerY = scale * (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI))

  const left = centerX - width / 2
  const right = centerX + width / 2
  const top = centerY - height / 2
  const bottom = centerY + height / 2

  function pxToLng(x: number): number {
    return (x / scale) * 360 - 180
  }
  function pxToLat(y: number): number {
    const n = Math.PI - (2 * Math.PI * y) / scale
    return (180 / Math.PI) * Math.atan(Math.sinh(n))
  }

  return {
    west: pxToLng(left),
    east: pxToLng(right),
    north: pxToLat(top),
    south: pxToLat(bottom),
  }
}

/** Build the Google Static Maps URL for a given fit. */
export function buildStaticMapUrl(center: LatLng, zoom: number, width: number, height: number, apiKey: string): string {
  const params = new URLSearchParams({
    center: `${center.lat},${center.lng}`,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: '2',
    maptype: 'roadmap',
    key: apiKey,
  })
  return `${STATIC_MAPS_BASE}?${params.toString()}`
}

/**
 * Given an array of points, compute everything we need to render the bottom
 * face of the cube: the static-map URL plus the actual bounds the image covers.
 * Returns null if there are no points.
 */
export type StaticMapFit = {
  url: string
  bounds: LatLngBounds
  center: LatLng
  zoom: number
  width: number
  height: number
}

export function fitStaticMap(points: LatLng[], apiKey: string): StaticMapFit | null {
  const dataBounds = computeBounds(points)
  if (!dataBounds) return null
  const width = MAX_IMAGE_DIM
  const height = MAX_IMAGE_DIM
  const center = boundsCenter(dataBounds)
  const zoom = fitBoundsZoom(dataBounds, width, height)
  const bounds = imageBoundsAt(center, zoom, width, height)
  const url = buildStaticMapUrl(center, zoom, width, height, apiKey)
  return { url, bounds, center, zoom, width, height }
}

/**
 * Project a (lat, lng) into a normalized [-0.5, 0.5] × [-0.5, 0.5] square,
 * matching the static map image's pixel grid (Web Mercator).
 *
 * Returns { u, v } where:
 *   u = -0.5 (west edge) → +0.5 (east edge)
 *   v = -0.5 (south edge) → +0.5 (north edge)
 *
 * Three.js callers map u → +X, v → -Z (north-is-negative-Z).
 */
export function projectToUnit(lat: number, lng: number, bounds: LatLngBounds): { u: number; v: number } {
  // Longitude is linear in Mercator.
  const u = (lng - bounds.west) / (bounds.east - bounds.west) - 0.5

  // Latitude needs the Mercator transform so it lines up with the texture.
  function mercY(latDeg: number): number {
    return Math.log(Math.tan(Math.PI / 4 + (latDeg * Math.PI) / 360))
  }
  const yNorm = (mercY(lat) - mercY(bounds.south)) / (mercY(bounds.north) - mercY(bounds.south))
  const v = yNorm - 0.5
  return { u, v }
}
